import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Stack,
  Grid,
  Checkbox,
  FormControlLabel
} from '@mui/material';
import { Schedule, Edit as EditIcon } from '@mui/icons-material';

interface ScheduleEditorProps {
  value: string;
  onChange: (cronExpression: string) => void;
  label?: string;
  fullWidth?: boolean;
}

interface ScheduleConfig {
  triggerType: 'daily' | 'weekly' | 'monthly' | 'once';
  startTime: string; // HH:MM format
  startDate: string; // YYYY-MM-DD
  interval: number; // repeat every X days/weeks
  daysOfWeek: number[]; // 0-6, 0=Sunday
  dayOfMonth: number; // 1-31
}

const DAYS_OF_WEEK = [
  { value: 0, label: 'Sunday' },
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
];

// Convert cron expression to schedule config
const cronToSchedule = (cron: string): ScheduleConfig => {
  const parts = cron.trim().split(/\s+/);
  if (parts.length !== 5) return getDefaultSchedule();

  const [minute, hour, dayOfMonth, , dayOfWeek] = parts;

  // Check for daily pattern
  if (dayOfMonth === '*' && dayOfWeek === '*') {
    // Daily or interval
    return {
      triggerType: 'daily',
      startTime: `${hour}:${minute}`,
      startDate: new Date().toISOString().split('T')[0],
      interval: 1,
      daysOfWeek: [],
      dayOfMonth: 1
    };
  }

  // Check for weekly pattern (specific days)
  if (dayOfMonth === '?' && dayOfWeek !== '*' && dayOfWeek !== '?') {
    const days = dayOfWeek.split(',').map(Number);
    return {
      triggerType: 'weekly',
      startTime: `${hour}:${minute}`,
      startDate: new Date().toISOString().split('T')[0],
      interval: 1,
      daysOfWeek: days,
      dayOfMonth: 1
    };
  }

  // Check for monthly pattern
  if (dayOfWeek === '?' && dayOfMonth !== '*') {
    return {
      triggerType: 'monthly',
      startTime: `${hour}:${minute}`,
      startDate: new Date().toISOString().split('T')[0],
      interval: 1,
      daysOfWeek: [],
      dayOfMonth: parseInt(dayOfMonth)
    };
  }

  return getDefaultSchedule();
};

// Convert schedule config to cron expression
const scheduleToCron = (config: ScheduleConfig): string => {
  const [hour, minute] = config.startTime.split(':');
  const { triggerType, interval, dayOfMonth, daysOfWeek } = config;

  switch (triggerType) {
    case 'daily':
      // Run every N days at specified time
      if (interval === 1) {
        return `${minute} ${hour} * * *`;
      }
      // For intervals > 1, we'd need to store multiple cron expressions or use advanced syntax
      return `${minute} ${hour} */${interval} * *`;

    case 'weekly':
      // Run on specific days of week
      if (daysOfWeek.length === 0) return `${minute} ${hour} * * 1`; // Default to Monday
      const daysOfWeekStr = daysOfWeek.sort().join(',');
      return `${minute} ${hour} * * ${daysOfWeekStr}`;

    case 'monthly':
      // Run on specific day of month
      return `${minute} ${hour} ${dayOfMonth} * *`;

    case 'once':
      // One time at specific date/time (not a repeating schedule)
      return `${minute} ${hour} ${parseInt(config.startDate.split('-')[2])} ${parseInt(config.startDate.split('-')[1])} ${parseInt(config.startDate.split('-')[0])}`;

    default:
      return `${minute} ${hour} * * *`;
  }
};

const getDefaultSchedule = (): ScheduleConfig => ({
  triggerType: 'daily',
  startTime: '00:00',
  startDate: new Date().toISOString().split('T')[0],
  interval: 1,
  daysOfWeek: [],
  dayOfMonth: 1
});

const getHumanReadableSchedule = (config: ScheduleConfig): string => {
  const time = new Date(`2000-01-01T${config.startTime}`).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });

  switch (config.triggerType) {
    case 'daily':
      return config.interval === 1
        ? `Daily at ${time}`
        : `Every ${config.interval} days at ${time}`;

    case 'weekly':
      if (config.daysOfWeek.length === 0) return `Weekly on Monday at ${time}`;
      if (config.daysOfWeek.length === 1) {
        return `Weekly on ${DAYS_OF_WEEK[config.daysOfWeek[0]].label} at ${time}`;
      }
      if (config.daysOfWeek.length === 7) return `Daily at ${time}`;
      const dayNames = config.daysOfWeek
        .sort((a, b) => a - b)
        .map(d => DAYS_OF_WEEK[d].label)
        .join(', ');
      return `Weekly on ${dayNames} at ${time}`;

    case 'monthly':
      return `Monthly on the ${config.dayOfMonth}${getOrdinalSuffix(config.dayOfMonth)} at ${time}`;

    case 'once':
      const date = new Date(config.startDate + 'T' + config.startTime);
      return `One time: ${date.toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })} at ${time}`;

    default:
      return 'Custom schedule';
  }
};

const getOrdinalSuffix = (day: number): string => {
  if (day > 3 && day < 21) return 'th';
  switch (day % 10) {
    case 1: return 'st';
    case 2: return 'nd';
    case 3: return 'rd';
    default: return 'th';
  }
};

const ScheduleEditor: React.FC<ScheduleEditorProps> = ({
  value,
  onChange,
  label = 'Schedule',
  fullWidth = true
}) => {
  const [config, setConfig] = useState<ScheduleConfig>(() => cronToSchedule(value));
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [advancedCron, setAdvancedCron] = useState(value);

  useEffect(() => {
    setAdvancedCron(value);
  }, [value]);

  const handleConfigChange = (newConfig: Partial<ScheduleConfig>) => {
    const updated = { ...config, ...newConfig };
    setConfig(updated);
    onChange(scheduleToCron(updated));
  };

  const handleAdvancedCronChange = (cron: string) => {
    setAdvancedCron(cron);
    onChange(cron);
  };

  const handleUseAdvanced = () => {
    setShowAdvanced(true);
  };

  const handleUseBasic = () => {
    setShowAdvanced(false);
    const newConfig = cronToSchedule(advancedCron);
    setConfig(newConfig);
    onChange(scheduleToCron(newConfig));
  };

  if (showAdvanced) {
    return (
    <Box sx={{ width: fullWidth ? '100%' : 'auto' }}>
      {label && (
        <Typography variant="body2" sx={{ mb: 1, color: '#858585', fontSize: '0.875rem', fontWeight: 500 }}>
          {label}
        </Typography>
      )}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          border: '1px solid #3c3c3c',
          backgroundColor: '#1e1e1e',
          borderRadius: 1
        }}
      >
        <Stack spacing={2}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="subtitle2" sx={{ color: '#cccccc', fontSize: '0.875rem', fontWeight: 600 }}>
              Advanced Cron Expression
            </Typography>
            <Button
              size="small"
              onClick={handleUseBasic}
              startIcon={<Schedule />}
              sx={{ color: '#007acc', fontSize: '0.75rem' }}
            >
              Switch to Basic
            </Button>
          </Stack>

          <TextField
            value={advancedCron}
            onChange={(e) => handleAdvancedCronChange(e.target.value)}
            label="Cron Expression"
            helperText="Format: minute hour day month day-of-week (e.g., 0 */6 * * * = every 6 hours)"
            fullWidth
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#3c3c3c',
                color: '#d4d4d4',
                '& fieldset': { borderColor: '#555' },
                '&:hover fieldset': { borderColor: '#007acc' },
                '&.Mui-focused fieldset': { borderColor: '#007acc' }
              },
              '& .MuiInputLabel-root': { color: '#cccccc', fontSize: '0.8rem' },
              '& .MuiFormHelperText-root': { color: '#858585', fontSize: '0.7rem' }
            }}
          />

          <Typography variant="caption" sx={{ color: '#858585', fontSize: '0.75rem' }}>
            <strong>Cron format examples:</strong><br />
            • 0 */6 * * * - Every 6 hours<br />
            • 0 0 * * * - Daily at midnight<br />
            • 0 9 * * 1-5 - Weekdays at 9 AM<br />
            • 0 0 1 * * - Monthly on the 1st
          </Typography>
        </Stack>
      </Paper>
    </Box>
  );
  }

  return (
    <Box sx={{ width: fullWidth ? '100%' : 'auto' }}>
      {label && (
        <Typography variant="body2" sx={{ mb: 1, color: '#858585', fontSize: '0.875rem', fontWeight: 500 }}>
          {label}
        </Typography>
      )}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          border: '1px solid #3c3c3c',
          backgroundColor: '#1e1e1e',
          borderRadius: 1
        }}
      >
        <Stack spacing={2}>
          {/* Summary */}
          <Box
            sx={{
              p: 1.5,
              backgroundColor: '#0e639c',
              borderRadius: 0.5,
              border: '1px solid #007acc'
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <Schedule sx={{ color: '#ffffff', fontSize: '1rem' }} />
              <Typography variant="body2" sx={{ color: '#ffffff', fontSize: '0.875rem', fontWeight: 500 }}>
                {getHumanReadableSchedule(config)}
              </Typography>
            </Stack>
          </Box>

          {/* Trigger Type */}
          <FormControl size="small" fullWidth>
            <InputLabel sx={{ fontSize: '0.8rem', color: '#cccccc' }}>Trigger</InputLabel>
            <Select
              value={config.triggerType}
              label="Trigger"
              onChange={(e) => handleConfigChange({ triggerType: e.target.value as any })}
              sx={{
                backgroundColor: '#3c3c3c',
                color: '#d4d4d4',
                '& .MuiOutlinedInput-notchedOutline': { borderColor: '#555' },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#007acc' },
                '& .MuiSelect-icon': { color: '#cccccc' }
              }}
            >
              <MenuItem value="daily">Daily</MenuItem>
              <MenuItem value="weekly">Weekly</MenuItem>
              <MenuItem value="monthly">Monthly</MenuItem>
              <MenuItem value="once">One Time</MenuItem>
            </Select>
          </FormControl>

          <Grid container spacing={1.5}>
            {/* Start Time */}
            <Grid item xs={6}>
              <TextField
                type="time"
                label="Start Time"
                value={config.startTime}
                onChange={(e) => handleConfigChange({ startTime: e.target.value })}
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#3c3c3c',
                    color: '#d4d4d4',
                    '& fieldset': { borderColor: '#555' },
                    '&:hover fieldset': { borderColor: '#007acc' },
                    '&.Mui-focused fieldset': { borderColor: '#007acc' }
                  },
                  '& .MuiInputLabel-root': { color: '#cccccc', fontSize: '0.8rem' }
                }}
              />
            </Grid>

            {/* Start Date */}
            <Grid item xs={6}>
              <TextField
                type="date"
                label="Start Date"
                value={config.startDate}
                onChange={(e) => handleConfigChange({ startDate: e.target.value })}
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#3c3c3c',
                    color: '#d4d4d4',
                    '& fieldset': { borderColor: '#555' },
                    '&:hover fieldset': { borderColor: '#007acc' },
                    '&.Mui-focused fieldset': { borderColor: '#007acc' }
                  },
                  '& .MuiInputLabel-root': { color: '#cccccc', fontSize: '0.8rem' }
                }}
              />
            </Grid>
          </Grid>

          {/* Daily Options */}
          {config.triggerType === 'daily' && (
            <Grid container spacing={1.5} alignItems="center">
              <Grid item xs={8}>
                <Typography variant="body2" sx={{ color: '#cccccc', fontSize: '0.8rem' }}>
                  Repeat every:
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <TextField
                  type="number"
                  value={config.interval}
                  onChange={(e) => handleConfigChange({ interval: parseInt(e.target.value) || 1 })}
                  size="small"
                  fullWidth
                  inputProps={{ min: 1, max: 365 }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#3c3c3c',
                      color: '#d4d4d4',
                      '& fieldset': { borderColor: '#555' },
                      '&:hover fieldset': { borderColor: '#007acc' },
                      '&.Mui-focused fieldset': { borderColor: '#007acc' }
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body2" sx={{ color: '#858585', fontSize: '0.75rem' }}>
                  day(s)
                </Typography>
              </Grid>
            </Grid>
          )}

          {/* Weekly Options */}
          {config.triggerType === 'weekly' && (
            <Box>
              <Typography variant="body2" sx={{ color: '#cccccc', fontSize: '0.8rem', mb: 1, fontWeight: 500 }}>
                Select Day(s):
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {DAYS_OF_WEEK.map((day) => (
                  <FormControlLabel
                    key={day.value}
                    control={
                      <Checkbox
                        size="small"
                        checked={config.daysOfWeek.includes(day.value)}
                        onChange={(e) => {
                          const newDays = e.target.checked
                            ? [...config.daysOfWeek, day.value]
                            : config.daysOfWeek.filter((d) => d !== day.value);
                          handleConfigChange({ daysOfWeek: newDays });
                        }}
                        sx={{
                          color: '#cccccc',
                          '&.Mui-checked': { color: '#007acc' },
                          '& .MuiCheckbox-root': { color: '#cccccc' }
                        }}
                      />
                    }
                    label={day.label}
                    sx={{ color: '#cccccc', fontSize: '0.8rem', mr: 0 }}
                  />
                ))}
              </Stack>
            </Box>
          )}

          {/* Monthly Options */}
          {config.triggerType === 'monthly' && (
            <Grid container spacing={1.5} alignItems="center">
              <Grid item xs={8}>
                <Typography variant="body2" sx={{ color: '#cccccc', fontSize: '0.8rem' }}>
                  On day:
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <TextField
                  type="number"
                  value={config.dayOfMonth}
                  onChange={(e) => handleConfigChange({ dayOfMonth: parseInt(e.target.value) || 1 })}
                  size="small"
                  fullWidth
                  inputProps={{ min: 1, max: 31 }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#3c3c3c',
                      color: '#d4d4d4',
                      '& fieldset': { borderColor: '#555' },
                      '&:hover fieldset': { borderColor: '#007acc' },
                      '&.Mui-focused fieldset': { borderColor: '#007acc' }
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body2" sx={{ color: '#858585', fontSize: '0.75rem' }}>
                  of each month
                </Typography>
              </Grid>
            </Grid>
          )}

          {/* Advanced Button */}
          <Button
            size="small"
            onClick={handleUseAdvanced}
            startIcon={<EditIcon />}
            sx={{
              alignSelf: 'flex-start',
              color: '#007acc',
              fontSize: '0.75rem',
              mt: 1
            }}
          >
            Advanced (Cron Expression)
          </Button>
        </Stack>
      </Paper>
    </Box>
  );
};

export default ScheduleEditor;
